import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader } from '../DataTable/DataTableColumnHeader';
import { DataTableRowActions } from '../DataTable/DataTableRowActions';
import type { RowAction } from '../DataTable/DataTableRowActions';
import { ACTIONS_COLUMN_WIDTH, WRAP_ANYWHERE_CLASS } from '../DataTable/constants';
import { ORDER_SOURCE_COLUMN_WIDTHS } from '../DataTable/columnWidths';
import { useTranslation } from '@hooks/useTranslation';
import { EditIcon } from '../icons';
import { DeactivateIcon } from '../icons/DeactivateIcon';
import { CheckCircleIcon } from '../icons/CheckCircleIcon';
import type { OrderSource } from './types';

interface CreateOrderSourceColumnsOptions {
  onStatusToggle: (id: number, active: boolean) => void;
  onEdit?: (id: number) => void;
  // RBAC: omit the Edit + Activate/Deactivate row actions for read-only users.
  showRowActions?: boolean;
}

function DescriptionCell({ description }: { description: string | null }) {
  if (!description) {
    return <span className="text-sm text-grey">—</span>;
  }
  return (
    <p className="text-sm text-off-black line-clamp-2 leading-[1.4]" title={description}>
      {description}
    </p>
  );
}

function ActionsCell({
  orderSource,
  onStatusToggle,
  onEdit,
}: {
  orderSource: OrderSource;
  onStatusToggle: (id: number, active: boolean) => void;
  onEdit: (id: number) => void;
}) {
  const { t } = useTranslation();

  const actions: RowAction[] = [
    {
      label: t('common.table.actions.edit'),
      icon: <EditIcon className="size-5" />,
      onClick: () => onEdit(orderSource.id),
    },
    orderSource.active
      ? {
          label: t('common.table.actions.deactivate', 'Deactivate'),
          icon: <DeactivateIcon className="size-5" />,
          onClick: () => onStatusToggle(orderSource.id, false),
        }
      : {
          label: t('common.table.actions.activate', 'Activate'),
          icon: <CheckCircleIcon className="size-3.5" />,
          onClick: () => onStatusToggle(orderSource.id, true),
        },
  ];

  return <DataTableRowActions actions={actions} />;
}

export function createOrderSourceColumns({
  onStatusToggle,
  onEdit,
  showRowActions = true,
}: CreateOrderSourceColumnsOptions): ColumnDef<OrderSource>[] {
  const columns: ColumnDef<OrderSource>[] = [
    {
      accessorKey: 'name',
      header: ({ column }) => <DataTableColumnHeader column={column} title="Name" />,
      size: ORDER_SOURCE_COLUMN_WIDTHS.name,
      meta: { frozen: true },
      cell: ({ row }) => (
        <div className="flex min-h-[48px] items-center">
          <span className={`text-sm font-semibold text-off-black ${WRAP_ANYWHERE_CLASS}`}>{row.original.name}</span>
        </div>
      ),
    },
    {
      accessorKey: 'fee_type',
      header: 'Fee type',
      size: ORDER_SOURCE_COLUMN_WIDTHS.feeType,
      enableSorting: false,
      cell: ({ row }) =>
        row.original.fee_type ? (
          <span className="text-sm text-off-black">{row.original.fee_type}</span>
        ) : (
          <span className="text-sm text-grey">—</span>
        ),
    },
    {
      accessorKey: 'fee',
      header: 'Fee',
      size: ORDER_SOURCE_COLUMN_WIDTHS.fee,
      enableSorting: false,
      cell: ({ row }) => {
        const { fee, fee_type } = row.original;
        if (fee === null || fee === undefined || fee === 0) {
          return <span className="text-sm text-grey">—</span>;
        }
        const formatted =
          fee_type === 'Percentage' ? `${fee}%` : fee_type === 'Flat' ? `$${fee}` : `${fee}`;
        return <span className="text-sm text-off-black">{formatted}</span>;
      },
    },
    {
      accessorKey: 'description',
      header: 'Description',
      size: ORDER_SOURCE_COLUMN_WIDTHS.description,
      enableSorting: false,
      cell: ({ row }) => <DescriptionCell description={row.original.description} />,
    },
  ];

  if (showRowActions) {
    columns.push({
      id: 'actions',
      enableSorting: false,
      size: ACTIONS_COLUMN_WIDTH,
      cell: ({ row }) => (
        <ActionsCell
          orderSource={row.original}
          onStatusToggle={onStatusToggle}
          onEdit={onEdit ?? (() => {})}
        />
      ),
    });
  }

  return columns;
}
